// Content-pack loader (client side). Imports the SAME repo-root JSON the backend
// validates with pydantic, and re-validates it here with zod so a bad pack fails
// loudly at startup instead of silently corrupting the sim. One source of truth
// across both languages → same seed → identical replay.
import { z } from 'zod'
import archetypesPackJson from '@content/packs/archetypes.json'
import combatPackJson from '@content/packs/combat.json'
import compositionPackJson from '@content/packs/composition.json'
import boonsPackJson from '@content/packs/boons.json'
import playerPackJson from '@content/packs/player.json'
import rosterPackJson from '@content/packs/roster.json'
import rulesPackJson from '@content/packs/rules.json'
import weaponsPackJson from '@content/packs/weapons.json'
import weatherPackJson from '@content/packs/weather.json'
import zonesPackJson from '@content/packs/zones.json'
import type { ArchetypeTemplate, ZoneDef } from './JKGame_content'
import type { Archetype, Passive, Weapon } from './JKGame_types'

const WeaponSchema = z.object({
  name: z.string().min(1),
  kind: z.enum(['N', 'P', 'K', 'G', 'C', 'D']),
  power: z.number().int().min(0),
  ammo: z.number().int(), // -1 = 無限（∞）
  range: z.enum(['S', 'M', 'L']),
})

const WeaponsPackSchema = z.object({
  schema: z.literal('weapons'),
  version: z.number().int(),
  weapons: z.array(WeaponSchema).min(1),
})

// Throws at import time if the pack is malformed (intentional — a broken pack is
// a build/content bug, not a runtime degradation).
const pack = WeaponsPackSchema.parse(weaponsPackJson)

// zod's inferred element type IS structurally `Weapon`; the assertion documents
// the contract without widening.
export const PACK_LOOT_WEAPONS: readonly Weapon[] = pack.weapons as readonly Weapon[]

const ZoneSchema = z.object({
  id: z.string().min(1),
  coord: z.string().regex(/^[A-J]-\d{2}$/),
  region: z.enum(['north', 'central', 'south']),
  danger: z.boolean(),
  theme: z.enum(['lighthouse', 'cliff', 'shrine', 'rock', 'forest', 'port', 'beach', 'water', 'building']),
})

const ZonesPackSchema = z.object({
  schema: z.literal('zones'),
  version: z.number().int(),
  zones: z.array(ZoneSchema).min(1),
})

const zonesPack = ZonesPackSchema.parse(zonesPackJson)

// Order == sim zone index (mirrors the backend); must not be reordered.
export const PACK_ZONES: readonly ZoneDef[] = zonesPack.zones as readonly ZoneDef[]

const WeatherTypeSchema = z.object({
  id: z.string().regex(/^w\d+$/),
  fog: z.boolean(),
})

const WeatherPackSchema = z.object({
  schema: z.literal('weather'),
  version: z.number().int(),
  types: z.array(WeatherTypeSchema).min(1),
})

const weatherPack = WeatherPackSchema.parse(weatherPackJson)

// Index == weather id (mirrors backend rng.int(0, count-1)); i18n key == id.
export const WEATHER_COUNT = weatherPack.types.length
export const WEATHER_FOG_INDEXES: readonly number[] = weatherPack.types
  .map((t, i) => (t.fog ? i : -1))
  .filter((i) => i >= 0)

// A stat is either a scalar (no RNG draw) or a [min,max] range (one rng.int).
// The distinction is sim-significant — keep it intact (see archetypes.json note).
const StatRangeSchema = z.union([z.number().int(), z.tuple([z.number().int(), z.number().int()])])
const StartWeaponSchema = z.union([z.literal('fists'), z.literal('random'), WeaponSchema])

const ArchetypeSchema = z.object({
  mhp: StatRangeSchema,
  msp: StatRangeSchema,
  att: StatRangeSchema,
  def: StatRangeSchema,
  skill: StatRangeSchema,
  armor: z.number().int().min(0),
  startWeapon: StartWeaponSchema,
})

const ArchetypesPackSchema = z.object({
  schema: z.literal('archetypes'),
  version: z.number().int(),
  archetypes: z.record(z.string(), ArchetypeSchema),
})

const archetypesPack = ArchetypesPackSchema.parse(archetypesPackJson)

// Keyed by archetype name (excluding 'player'); mirrors the backend dict.
export const PACK_ARCHETYPES = archetypesPack.archetypes as Record<
  Exclude<Archetype, 'player'>,
  ArchetypeTemplate
>

const PlayerPackSchema = z.object({
  schema: z.literal('player'),
  version: z.number().int(),
  player: z.object({
    mhp: z.number().int().min(1),
    msp: z.number().int().min(1),
    att: z.number().int().min(0),
    def: z.number().int().min(0),
    skill: z.number().int().min(0),
    armor: z.number().int().min(0),
    weapon: WeaponSchema,
  }),
})

const playerPack = PlayerPackSchema.parse(playerPackJson)

// Player start template (all scalars, no RNG on spawn); mirrors the backend.
export interface PlayerTemplate {
  mhp: number
  msp: number
  att: number
  def: number
  skill: number
  armor: number
  weapon: Weapon
}

export const PACK_PLAYER_TEMPLATE: PlayerTemplate = playerPack.player as PlayerTemplate

const CompositionEntrySchema = z.object({
  archetype: z.enum(['survivor', 'looter', 'hunter', 'medic', 'veteran', 'boss']),
  count: z.number().int().min(0),
})

const CompositionPackSchema = z.object({
  schema: z.literal('composition'),
  version: z.number().int(),
  composition: z.array(CompositionEntrySchema).min(1),
})

const compositionPack = CompositionPackSchema.parse(compositionPackJson)

// Ordered (archetype, count); order == roster build order (pre-shuffle), mirrors
// the backend — must not be reordered (same-seed determinism).
export const PACK_COMPOSITION: ReadonlyArray<[Archetype, number]> = compositionPack.composition.map(
  (e) => [e.archetype, e.count] as [Archetype, number],
)

const KindNumber = z.record(z.enum(['N', 'P', 'K', 'G', 'C', 'D']), z.number())
const CombatPackSchema = z.object({
  schema: z.literal('combat'),
  version: z.number().int(),
  damage_scale: z.number(),
  rage_special: z.number().int().min(0),
  hit_base: KindNumber,
  skill_dmg: KindNumber,
  injury_rate: KindNumber,
  injury_parts: z.record(z.enum(['N', 'P', 'K', 'G', 'C', 'D']), z.array(z.enum(['b', 'h', 'a', 'f']))),
})

const combatPack = CombatPackSchema.parse(combatPackJson)

const RulesPackSchema = z.object({
  schema: z.literal('rules'),
  version: z.number().int(),
  area_interval: z.number().int().min(1),
  area_add: z.number().int().min(1),
  bot_count: z.number().int().min(1),
  move_cost: z.number().int().min(0),
  move_cost_hurt: z.number().int().min(0),
})

const rulesPack = RulesPackSchema.parse(rulesPackJson)

// Single-match rule constants (mirrors the backend).
export const PACK_RULES = {
  areaInterval: rulesPack.area_interval,
  areaAdd: rulesPack.area_add,
  botCount: rulesPack.bot_count,
  moveCost: rulesPack.move_cost,
  moveCostHurt: rulesPack.move_cost_hurt,
}

// ── Roster (出戰角色 loadout) ────────────────────────────────────────────────
// Roguelite loadout pack: each character → {startWeapon, passive, unlock}. The sim
// only consumes the resolved Loadout (weapon + passive id); display names live in i18n
// (char.<id>.name / passive.<id>). Backend pydantic parity is flagged for P1 — until then
// loadout is applied client-side in createMatch (RNG-free → same-seed determinism intact).
const RosterCharSchema = z.object({
  id: z.string().min(1),
  startWeapon: WeaponSchema,
  passive: z.enum(['none', 'first_blood']),
  unlock: z.string().min(1), // free | gacha | souls:N
})

const RosterPackSchema = z.object({
  schema: z.literal('roster'),
  version: z.number().int(),
  characters: z.array(RosterCharSchema).min(1),
})

const rosterPack = RosterPackSchema.parse(rosterPackJson)

export interface RosterChar {
  id: string
  startWeapon: Weapon
  passive: Passive
  unlock: string
}

export interface Loadout {
  weapon: Weapon
  passive: Passive
}

export const PACK_ROSTER: readonly RosterChar[] = rosterPack.characters as readonly RosterChar[]

// Resolve a character id → its loadout, or null if unknown (caller falls back to the
// default player template). Pure + RNG-free so createMatch can apply it without forking
// the deterministic RNG stream.
export function resolveLoadout(charId: string | null | undefined): Loadout | null {
  if (!charId) return null
  const c = PACK_ROSTER.find((r) => r.id === charId)
  return c ? { weapon: c.startWeapon, passive: c.passive } : null
}

// ── Boons (局內 roguelite 強化) ──────────────────────────────────────────────
// Run-scoped power-ups offered three-at-a-time after each player kill. Stat is one of the
// player entity's additive stats; amount is the bonus. mhp/msp also top up the current pool
// by the same amount on apply (so the gain is felt immediately). Backend parity flagged for P.
const BoonSchema = z.object({
  id: z.string().min(1),
  stat: z.enum(['att', 'def', 'skill', 'armor', 'mhp', 'msp']),
  amount: z.number().int(),
})

const BoonsPackSchema = z.object({
  schema: z.literal('boons'),
  version: z.number().int(),
  boons: z.array(BoonSchema).min(3), // need ≥3 for a three-choice offer
})

const boonsPack = BoonsPackSchema.parse(boonsPackJson)

export type BoonStat = 'att' | 'def' | 'skill' | 'armor' | 'mhp' | 'msp'
export interface Boon {
  id: string
  stat: BoonStat
  amount: number
}

export const PACK_BOONS: readonly Boon[] = boonsPack.boons as readonly Boon[]

export function boonById(id: string): Boon | undefined {
  return PACK_BOONS.find((b) => b.id === id)
}

// Combat tuning (mirrors the backend; values must match the sim exactly).
export const PACK_COMBAT = {
  damageScale: combatPack.damage_scale,
  rageSpecial: combatPack.rage_special,
  hitBase: combatPack.hit_base,
  skillDmg: combatPack.skill_dmg,
  injuryRate: combatPack.injury_rate,
  injuryParts: combatPack.injury_parts,
}
